import forge from 'apis/forge';
import { DesignSystem } from 'dragonmouse-shared/interfaces';
import { shallowEqual, useSelector } from 'react-redux';
import {
	selectActiveScene,
	selectActiveSceneDesignSystem,
	selectActiveSceneLinkedDesignSystemVersion,
	selectActiveSceneLatestDesignSystemVersion,
} from 'redux/db/reducers';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import { useNavigate } from 'react-router';
import * as routes from 'routes';
import { Loader } from 'components/Widget/Loader';
import { useState } from 'react';
import { canEdit } from 'redux/db/util';
import { displayViewOnlyModal } from 'redux/mode/util';
import * as S from './styles';

interface ActiveDesignSystemSectionProps {
	onLinkDs: (designSystem: DesignSystem | null) => Promise<void>;
	onClose: () => void;
	updatePendingState?: 'publishing' | 'ready' | null;
}

const ActiveDesignSystemSection = ({
	onLinkDs,
	onClose,
	updatePendingState = null,
}: ActiveDesignSystemSectionProps) => {
	const navigate = useNavigate();
	const [isUpgrading, setIsUpgrading] = useState(false);
	const { scene, activeDesignSystem, linkedVersion, latestVersion, isMobile } = useSelector(
		(r: RootReducer) => {
			return {
				scene: selectActiveScene(r.db, r.mode.activeSceneId),
				activeDesignSystem: selectActiveSceneDesignSystem(r.db, r.mode.activeSceneId),
				linkedVersion: selectActiveSceneLinkedDesignSystemVersion(r.db),
				latestVersion: selectActiveSceneLatestDesignSystemVersion(r.db),
				isMobile: selectIsMobile(r.mode),
			};
		},
		shallowEqual
	);

	if (!activeDesignSystem) return null;

	// Calculate version display
	const dsVersion = linkedVersion ? `v${linkedVersion.versionNumber}` : '';
	const isLatest =
		linkedVersion &&
		latestVersion &&
		linkedVersion.versionNumber === latestVersion.versionNumber &&
		latestVersion.manifest?.promptGenerationStatus === 'complete';

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onEdit = () => {
		navigate(routes.designSystem(activeDesignSystem.id, activeDesignSystem.title));
		onClose();
	};

	const onUnlink = () => {
		onLinkDs(null);
	};

	const onUpgrade = async () => {
		if (!scene || !activeDesignSystem) return;
		if (!canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}

		setIsUpgrading(true);
		try {
			// Re-link the same design system, which will update to the latest published version
			await forge.linkSceneDesignSystem({
				sceneId: scene.id,
				designSystemId: activeDesignSystem.id,
			});
			onClose();
		} catch (error) {
			// Error is already handled by alertOnError wrapper
			console.error('Failed to upgrade design system version:', error);
		} finally {
			setIsUpgrading(false);
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	return (
		<>
			<S.Base>
				<S.Header>
					<S.CheckIcon>✓</S.CheckIcon>
					<S.Name>{activeDesignSystem.title}</S.Name>
					{dsVersion && (
						<S.VersionBadge>{isLatest ? `Latest (${dsVersion})` : dsVersion}</S.VersionBadge>
					)}
				</S.Header>
			</S.Base>
			<S.Buttons>
				{!isMobile && (
					<S.Button primary onClick={onEdit}>
						Edit System
					</S.Button>
				)}
				<S.Button onClick={onUnlink}>Unlink</S.Button>
			</S.Buttons>
			{updatePendingState && linkedVersion && latestVersion && (
				<S.VersionSection>
					<S.SectionLabel>Version Management</S.SectionLabel>
					<S.VersionItem>
						<S.VersionDot green />
						<S.VersionInfo>
							<S.VersionLabel>{dsVersion} (Current)</S.VersionLabel>
						</S.VersionInfo>
					</S.VersionItem>
					<S.VersionItem>
						<S.VersionDot blue />
						<S.VersionInfo>
							<S.VersionLabel>
								v{latestVersion.versionNumber} (Latest)
								{updatePendingState === 'ready' && <S.ReadyBadge>READY</S.ReadyBadge>}
							</S.VersionLabel>
						</S.VersionInfo>
					</S.VersionItem>
					{updatePendingState === 'publishing' ? (
						<S.ProcessingButton disabled>
							<Loader width={12} height={12} />
							Preparing v{latestVersion.versionNumber} for use
						</S.ProcessingButton>
					) : (
						<S.UpdateButton onClick={onUpgrade} disabled={isUpgrading}>
							{isUpgrading ? (
								<>
									<Loader width={12} height={12} />
									Updating to v{latestVersion.versionNumber}...
								</>
							) : (
								'Update Project'
							)}
						</S.UpdateButton>
					)}
				</S.VersionSection>
			)}
		</>
	);
};

export default ActiveDesignSystemSection;
